'use client';

import React, { useState } from 'react';
import LandingNav from '@/components/LandingNav';
import LandingFooter from '@/app/components/LandingFooter';
import { Building2, Users, Award, TrendingUp, BookOpen, Landmark, Settings, CheckCircle, Send } from 'lucide-react';

const services = [
  {
    icon: Building2,
    title: 'Estate Agency Training',
    desc: 'Comprehensive training programmes for estate agencies — from foundational practice to advanced sales, negotiation, and client management skills.',
    outcomes: ['NQF-aligned content', 'Practical case studies', 'Team-based delivery'],
  },
  {
    icon: Users,
    title: 'Candidate Property Practitioner Programmes',
    desc: 'Structured learning pathways for candidate property practitioners completing their logbook requirements and preparing for professional registration.',
    outcomes: ['PPRA-aligned content', 'Mentorship integration', 'Assessment support'],
  },
  {
    icon: Award,
    title: 'Continuing Professional Development',
    desc: 'CPD programmes designed for registered property practitioners to maintain and advance their professional standing and market knowledge.',
    outcomes: ['CPD-structured modules', 'Flexible scheduling', 'Certificate of completion'],
  },
  {
    icon: TrendingUp,
    title: 'Executive Real Estate Education',
    desc: 'Senior leadership programmes for property executives, directors, and principals — focused on strategy, governance, and market leadership.',
    outcomes: ['Executive cohort format', 'Industry peer network', 'Strategic frameworks'],
  },
  {
    icon: BookOpen,
    title: 'Property Development Workshops',
    desc: 'Intensive workshops covering the full development cycle — from site identification and feasibility through to construction management and disposal.',
    outcomes: ['Practical project work', 'Developer-led sessions', 'Financial modelling'],
  },
  {
    icon: Landmark,
    title: 'Municipal & Public Sector Property Training',
    desc: 'Specialist programmes for municipal officials, government entities, and public sector property managers responsible for state-owned assets.',
    outcomes: ['Public sector context', 'Compliance frameworks', 'Asset management focus'],
  },
  {
    icon: Settings,
    title: 'Custom Corporate Programmes',
    desc: 'Bespoke training solutions designed around your organisation\'s specific needs, culture, and strategic objectives — delivered on-site or online.',
    outcomes: ['Needs assessment included', 'Branded delivery available', 'Flexible format'],
  },
];

interface FormState {
  organisation: string;
  name: string;
  role: string;
  email: string;
  phone: string;
  service: string;
  teamSize: string;
  message: string;
}

export default function CorporateTrainingPage() {
  const [form, setForm] = useState<FormState>({
    organisation: '',
    name: '',
    role: '',
    email: '',
    phone: '',
    service: '',
    teamSize: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 1200));
    setSubmitting(false);
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <LandingNav />

      {/* Hero */}
      <section className="relative pt-36 pb-24 bg-primary overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #1a2b22 0%, #22382c 60%, #2f4f3c 100%)' }} />
        <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent 0%, #c9a84c 30%, #e8c96a 50%, #c9a84c 70%, transparent 100%)' }} />
        <div className="relative z-10 max-w-screen-xl mx-auto px-6 lg:px-14 xl:px-20">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px w-12 bg-accent/60" />
            <span className="font-sans text-[0.65rem] font-semibold tracking-widest uppercase text-accent/80">Enterprise & Corporate</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="font-serif text-white mb-6" style={{ fontSize: 'clamp(2rem, 4.5vw, 3.5rem)', lineHeight: 1.1, letterSpacing: '-0.02em' }}>
                Corporate &<br />
                <span className="italic text-accent">Enterprise Training</span>
              </h1>
              <p className="font-sans text-white/65 leading-relaxed mb-8" style={{ fontSize: '1.0625rem' }}>
                REUSA delivers professional real estate education to organisations, agencies, and public sector
                entities across South Africa and the African continent. From candidate practitioners to executive
                leadership — we build capability at every level.
              </p>
              <div className="flex flex-wrap gap-3">
                {['Estate Agencies', 'Property Developers', 'Financial Institutions', 'Public Sector', 'Corporate Occupiers'].map((tag) => (
                  <span key={tag} className="font-sans text-[0.65rem] font-semibold tracking-wide px-3 py-1.5 rounded-full border border-white/20 text-white/60">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Service Lines', value: '7' },
                { label: 'Delivery Formats', value: '3' },
                { label: 'Sectors Served', value: 'All' },
                { label: 'Custom Programmes', value: '✓' },
              ].map((stat) => (
                <div key={stat.label} className="bg-white/5 border border-white/10 rounded-xl p-5 text-center">
                  <div className="font-serif font-bold text-3xl text-accent mb-1">{stat.value}</div>
                  <div className="font-sans text-xs text-white/50 tracking-wide">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-background">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-14 xl:px-20">
          <div className="max-w-2xl mb-14">
            <div className="flex items-center gap-3 mb-5">
              <div className="divider-gold" />
              <span className="label-caps">Training Services</span>
            </div>
            <h2 className="font-serif text-section-title text-foreground mb-4">
              Programmes for Every<br />
              <span className="italic">Level of the Organisation</span>
            </h2>
            <p className="font-sans text-sm text-muted-foreground leading-relaxed">
              From entry-level candidate practitioners to senior executives — REUSA delivers structured,
              industry-informed education that builds real capability across your organisation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {services.slice(0, 6).map((service) => (
              <div key={service.title} className="bg-card border border-border rounded-2xl p-6 luxury-shadow group hover:border-accent/40 transition-all duration-300">
                <div className="w-11 h-11 rounded-xl bg-secondary flex items-center justify-center mb-5 group-hover:bg-primary transition-colors duration-300">
                  <service.icon size={20} className="text-primary group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="font-serif font-semibold text-base text-foreground mb-2">{service.title}</h3>
                <p className="font-sans text-sm text-muted-foreground leading-relaxed mb-4">{service.desc}</p>
                <div className="pt-4 border-t border-border space-y-1.5">
                  {service.outcomes.map((o) => (
                    <div key={o} className="flex items-center gap-2">
                      <CheckCircle size={12} className="text-accent flex-shrink-0" />
                      <span className="font-sans text-xs text-muted-foreground">{o}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Custom programmes — full width */}
          <div className="bg-secondary border border-border rounded-2xl p-8 flex flex-col md:flex-row gap-6 items-start">
            <div className="w-11 h-11 rounded-xl bg-card border border-border flex items-center justify-center flex-shrink-0">
              <Settings size={20} className="text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-serif font-semibold text-lg text-foreground mb-2">{services[6].title}</h3>
              <p className="font-sans text-sm text-muted-foreground leading-relaxed mb-4">{services[6].desc}</p>
              <div className="flex flex-wrap gap-4">
                {services[6].outcomes.map((o) => (
                  <div key={o} className="flex items-center gap-2">
                    <CheckCircle size={12} className="text-accent flex-shrink-0" />
                    <span className="font-sans text-xs text-muted-foreground">{o}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enquiry Form */}
      <section className="py-20 bg-secondary border-t border-border" id="enquire">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-14 xl:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-14">
            {/* Left */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-5">
                <div className="divider-gold" />
                <span className="label-caps">Get in Touch</span>
              </div>
              <h2 className="font-serif text-section-title text-foreground mb-5">
                Speak to Our<br />
                <span className="italic">Corporate Training Team</span>
              </h2>
              <p className="font-sans text-sm text-muted-foreground leading-relaxed mb-8">
                Tell us about your organisation and training needs. Our corporate team will respond
                within two business days with a tailored proposal.
              </p>
              <div className="space-y-4">
                {[
                  { title: 'Needs Assessment', desc: 'We begin with a detailed assessment of your organisation\'s training requirements.' },
                  { title: 'Tailored Proposal', desc: 'A bespoke programme proposal designed around your objectives and budget.' },
                  { title: 'Flexible Delivery', desc: 'On-site, online, or blended delivery — scheduled around your team.' },
                ].map((step, i) => (
                  <div key={step.title} className="flex gap-4">
                    <div className="w-7 h-7 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0 font-sans text-xs font-bold">
                      {i + 1}
                    </div>
                    <div>
                      <p className="font-sans font-semibold text-sm text-foreground mb-0.5">{step.title}</p>
                      <p className="font-sans text-xs text-muted-foreground leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <div className="bg-card border border-border rounded-2xl p-8 luxury-shadow">
                {submitted ? (
                  <div className="text-center py-10">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-5">
                      <CheckCircle size={28} className="text-primary" />
                    </div>
                    <h3 className="font-serif font-semibold text-xl text-foreground mb-3">Enquiry Received</h3>
                    <p className="font-sans text-sm text-muted-foreground max-w-sm mx-auto leading-relaxed">
                      Thank you for your enquiry. Our corporate training team will be in touch within two business days.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block font-sans text-xs font-semibold text-foreground mb-1.5 tracking-wide">Organisation *</label>
                        <input
                          type="text"
                          name="organisation"
                          value={form.organisation}
                          onChange={handleChange}
                          required
                          placeholder="Company or agency name"
                          className="w-full px-4 py-3 rounded-xl border border-border bg-background font-sans text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                        />
                      </div>
                      <div>
                        <label className="block font-sans text-xs font-semibold text-foreground mb-1.5 tracking-wide">Your Name *</label>
                        <input
                          type="text"
                          name="name"
                          value={form.name}
                          onChange={handleChange}
                          required
                          placeholder="Full name"
                          className="w-full px-4 py-3 rounded-xl border border-border bg-background font-sans text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block font-sans text-xs font-semibold text-foreground mb-1.5 tracking-wide">Role / Title</label>
                        <input
                          type="text"
                          name="role"
                          value={form.role}
                          onChange={handleChange}
                          placeholder="e.g. Principal, HR Manager"
                          className="w-full px-4 py-3 rounded-xl border border-border bg-background font-sans text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                        />
                      </div>
                      <div>
                        <label className="block font-sans text-xs font-semibold text-foreground mb-1.5 tracking-wide">Email Address *</label>
                        <input
                          type="email"
                          name="email"
                          value={form.email}
                          onChange={handleChange}
                          required
                          placeholder="work@company.co.za"
                          className="w-full px-4 py-3 rounded-xl border border-border bg-background font-sans text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block font-sans text-xs font-semibold text-foreground mb-1.5 tracking-wide">Phone Number</label>
                        <input
                          type="tel"
                          name="phone"
                          value={form.phone}
                          onChange={handleChange}
                          placeholder="+27 ..."
                          className="w-full px-4 py-3 rounded-xl border border-border bg-background font-sans text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                        />
                      </div>
                      <div>
                        <label className="block font-sans text-xs font-semibold text-foreground mb-1.5 tracking-wide">Team Size</label>
                        <select
                          name="teamSize"
                          value={form.teamSize}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl border border-border bg-background font-sans text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                        >
                          <option value="">Select team size</option>
                          <option value="1-5">1–5 people</option>
                          <option value="6-20">6–20 people</option>
                          <option value="21-50">21–50 people</option>
                          <option value="51-100">51–100 people</option>
                          <option value="100+">100+ people</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block font-sans text-xs font-semibold text-foreground mb-1.5 tracking-wide">Training Service of Interest</label>
                      <select
                        name="service"
                        value={form.service}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-border bg-background font-sans text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                      >
                        <option value="">Select a service</option>
                        <option value="estate-agency">Estate Agency Training</option>
                        <option value="cpp">Candidate Property Practitioner Programmes</option>
                        <option value="cpd">Continuing Professional Development</option>
                        <option value="executive">Executive Real Estate Education</option>
                        <option value="development">Property Development Workshops</option>
                        <option value="public-sector">Municipal & Public Sector Property Training</option>
                        <option value="custom">Custom Corporate Programme</option>
                      </select>
                    </div>
                    <div>
                      <label className="block font-sans text-xs font-semibold text-foreground mb-1.5 tracking-wide">Tell Us About Your Needs</label>
                      <textarea
                        name="message"
                        value={form.message}
                        onChange={handleChange}
                        rows={4}
                        placeholder="Describe your training objectives, timeline, and any specific requirements..."
                        className="w-full px-4 py-3 rounded-xl border border-border bg-background font-sans text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="btn-gold w-full py-4 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                    >
                      {submitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                          Sending Enquiry...
                        </>
                      ) : (
                        <>
                          <Send size={15} />
                          Submit Corporate Enquiry
                        </>
                      )}
                    </button>
                    <p className="font-sans text-xs text-muted-foreground text-center">
                      We respond to all corporate enquiries within two business days.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
}
